import React from "react"
import dayjs from 'dayjs'
import 'dayjs/locale/cs'
import Box from "@mui/material/Box"
import Select from "@mui/material/Select"
import MenuItem from "@mui/material/MenuItem"
import FormControl from "@mui/material/FormControl"
import InputLabel from "@mui/material/InputLabel"
import { useForm, Controller, SubmitHandler } from "react-hook-form"
import { TimePicker } from '@mui/x-date-pickers/TimePicker'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'

import type { DoseFormInput } from "../../../types"
import CustomButton from "../../custom-button"
import { DOSES_IN_PICKER } from "../../../constants"
import { useCreateDose } from "../../../hooks/use-dose"
import FormRow from "../helpers/form-row"

export default function CreateDoseForm({
    appliancePlanId,
    selectedDay,
    handleClose,
    estateId,
}: {
    appliancePlanId: number,
    selectedDay: 1|2|3|4|5|6|0,
    handleClose: () => void,
    estateId: number
}) {
    const { control, handleSubmit } = useForm({
        defaultValues: {
            begins_at: dayjs('2024-03-21T00:00'),
            ends_at: dayjs('2024-03-21T01:00'),
            shower_count: 0,
            bath_count: 0,
        },
    })

    const mutation = useCreateDose(estateId, appliancePlanId, selectedDay, handleClose)

    const onSubmit: SubmitHandler<DoseFormInput> = data => {
        mutation.mutate(data)
    }

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
            }}>
                <FormRow>
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="cs">
                        <Controller
                            name="begins_at"
                            control={control}
                            render={({ field }) =>
                                <TimePicker label="Začátek" ampm={false} {...field}/>
                            }
                        />
                    </LocalizationProvider>
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="cs">
                        <Controller
                            name="ends_at"
                            control={control}
                            render={({ field }) =>
                                <TimePicker label="Konec" ampm={false} {...field}/>
                            }
                        />
                    </LocalizationProvider>
                </FormRow>
                <FormRow>
                    <FormControl variant="standard" sx={{ flexGrow: 1 }}>
                        <InputLabel id="dose-shower-count-label">Počet sprchování</InputLabel>
                        <Controller
                            name="shower_count"
                            control={control}
                            render={({ field }) =>
                                <Select
                                    labelId="dose-shower-count-label"
                                    id="dose-shower-count"
                                    {...field}
                                >
                                    {DOSES_IN_PICKER.map(count => (
                                        <MenuItem key={count} value={count}>{count}</MenuItem>
                                    ))}
                                </Select>
                            }
                        />
                    </FormControl>
                    <FormControl variant="standard" sx={{ flexGrow: 1 }}>
                        <InputLabel id="dose-bath-count-label">Počet koupání</InputLabel>
                        <Controller
                            name="bath_count"
                            control={control}
                            render={({ field }) =>
                                <Select
                                    labelId="dose-bath-count-label"
                                    id="dose-bath-count"
                                    {...field}
                                >
                                    {DOSES_IN_PICKER.map(count => (
                                        <MenuItem key={count} value={count}>{count}</MenuItem>
                                    ))}
                                </Select>
                            }
                        />
                    </FormControl>
                </FormRow>
                <Box display="flex" justifyContent="center" mt={2}>
                    <CustomButton
                        text="Vytvořit"
                        type="submit"
                        disabled={mutation.isPending}
                    />
                </Box>
                {mutation.isError ? (
                    <div>Došlo k chybě: {mutation.error.message}</div>
                ) : null}
            </Box>
        </form>
    )
}
